import { Component, Inject, ViewChild, ElementRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ImageCroppedEvent } from 'ngx-image-cropper';
import { DesignUtilityServices } from 'src/app/appServices/design-utility.service';

@Component({
  selector: 'app-upload-image',
  templateUrl: './upload-image.component.html',
  styleUrls: ['./upload-image.component.scss']
})
export class UploadImageComponent {
  imageChangedEvent: any = '';
  croppedImage: any = '';
  initialImage: string;

  @ViewChild('inpFile') inpFile: ElementRef;

  constructor(
    public dialogRef: MatDialogRef<UploadImageComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private _du: DesignUtilityServices,
  ) {
    this.initialImage = data.initialImage || '';
    if (this.initialImage) {
      this.croppedImage = this.initialImage;
      this.data.croppedImage = this.croppedImage;
    }
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  imageLoaded(): void {
    console.log('Image loaded successfully!');
  }
  
  cropperReady(): void {
    console.log('Cropper is Ready!');
  }

  loadImageFailed(): void {
    console.log('Image load failed!');
  }

  fileChangeEvent(event: any): void {
    const file = event.target.files[0];
    if (!file) return;
    
    // Check file size (5 MB max before processing)
    const maxSize = 5 * 1024 * 1024; // 5 MB
    if (file.size > maxSize) {
      this._du.openCustomSnackBar(
        'File size exceeds 5 MB. Please choose a smaller image.',
        'Close',
        'error'
      );
      return;
    }
    
    this.imageChangedEvent = event;
  } 

  async imageCropped(event: ImageCroppedEvent) {
    // Compress and convert to WebP/JPEG
    const optimizedImage = await this.compressAndConvertImage(event.base64);
    this.croppedImage = optimizedImage;
    this.data.croppedImage = optimizedImage;
  }

  removeImage(): void {
    this.imageChangedEvent = '';
    this.croppedImage = null;
    this.initialImage = '';
    this.data.croppedImage = null;
  }

  // Drag and Upload
  public dragOver = false;
  
  onDropFile(event: any) {
    const file = event.dataTransfer.files[0];
    if (!file) return;
    
    // Check file size (5 MB max before processing)
    const maxSize = 5 * 1024 * 1024; // 5 MB
    if (file.size > maxSize) {
      this._du.openCustomSnackBar(
        'File size exceeds 5 MB. Please choose a smaller image.',
        'Close',
        'error'
      );
      this.dragOver = false;
      return;
    }
    
    this.imageChangedEvent = { target: { files: [file] } };
    event.stopPropagation();
    event.preventDefault();
    this.dragOver = false;
  }

  onDragOverFile(event: any) {
    this.dragOver = true;
    event.stopPropagation();
    event.preventDefault();
  }

  onDragLeaveFile() {
    this.dragOver = false;
  }

  get aspectRatio() {
    return this.data.aspectRatio || 4 / 3;
  }

  get resizedWidth() {
    return this.data.resizedWidth || 0;
  }

  /**
   * Check if browser supports WebP format
   */
  private checkWebPSupport(): Promise<boolean> {
    return new Promise((resolve) => {
      const webP = new Image();
      webP.onload = webP.onerror = () => {
        resolve(webP.height === 2);
      };
      webP.src = 'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA';
    });
  }

  /**
   * Convert image to WebP format with compression
   */
  private async convertToWebP(base64Image: string, quality: number = 0.85): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result as string);
              reader.readAsDataURL(blob);
            } else {
              // Fallback to JPEG if WebP conversion fails
              this.compressToJPEG(base64Image, quality).then(resolve).catch(reject);
            }
          },
          'image/webp',
          quality
        );
      };
      img.onerror = reject;
      img.src = base64Image;
    });
  }

  /**
   * Compress image to JPEG format
   */
  private async compressToJPEG(base64Image: string, quality: number = 0.85): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        canvas.toBlob(
          (blob) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.readAsDataURL(blob);
          },
          'image/jpeg',
          quality
        );
      };
      img.onerror = reject;
      img.src = base64Image;
    });
  }

  /**
   * Keep alpha channel when WebP is not available
   */
  private async compressToPNG(base64Image: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('PNG conversion failed'));
              return;
            }
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.readAsDataURL(blob);
          },
          'image/png'
        );
      };
      img.onerror = reject;
      img.src = base64Image;
    });
  }

  /**
   * Detect transparent pixels in image
   */
  private async hasTransparency(base64Image: string): Promise<boolean> {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) {
          resolve(false);
          return;
        }

        ctx.drawImage(img, 0, 0);
        const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
        for (let i = 3; i < data.length; i += 4) {
          if (data[i] < 255) {
            resolve(true);
            return;
          }
        }
        resolve(false);
      };
      img.onerror = () => resolve(false);
      img.src = base64Image;
    });
  }

  /**
   * Compress and convert image to optimal format
   */
  private async compressAndConvertImage(base64: string): Promise<string> {
    const transparentImage = await this.hasTransparency(base64);
    const webpSupported = await this.checkWebPSupport();

    // WebP keeps transparency and is smaller than PNG in most cases.
    if (webpSupported) {
      return await this.convertToWebP(base64, 0.85);
    }

    // Fallback for browsers without WebP support:
    // keep PNG for transparent images, JPEG for opaque photos.
    if (transparentImage) {
      return await this.compressToPNG(base64);
    }

    return await this.compressToJPEG(base64, 0.85);
  }
}